import { WorkCase } from '@/data/types';

const developmentOfRobinwichmannCom: WorkCase = {
  id: "robinwichmann-com",
  slug: "development-of-robinwichmann.com",
  title: "Development of robinwichmann.com",
  description: "Concept and development of my personal portfolio website, built with modern web technologies and tuned for performance.",
  tags: ["Next.js", "React", "TypeScript", "TailwindCSS", "Framer Motion", "Cloudflare Pages", "Responsive Design", "SSR"],
  image: "/assets/img/work/dev_of_robinwichmann_com.webp",
  articleType: "DEVLOG",
  date: "2025-05",
  detailContent: {
    subtitle: "Concept and development of a modern portfolio website with Next.js and React",
    challenge: [
      {
        text: "As a web developer, I wanted to build a personal portfolio website that doesn't just showcase my skills but is itself an example of them: modern, interactive, and technically clean."
      },
      {
        text: "The biggest challenge was bringing all of my requirements together:"
      },
      {
        listing: [
          "A modern, appealing user interface with an intuitive, lively design that invites visitors to explore and play around",
          "Fast load times and optimal performance",
          "Responsive design for all devices",
          "SEO optimization",
          "Easy maintainability and extensibility",
          "Accessibility in line with WCAG standards"
        ]
      }
    ],
    solution: [
      {
        text: "I went with a modern tech stack that delivers performance, developer experience, and future-proofing:"
      },
      {
        subheadline: "1. Frontend framework",
        text: "I chose Next.js 15 as the framework, offering server-side rendering (SSR), static site generation, and optimized performance. Combined with React 19, it enabled efficient component-based development."
      },
      {
        subheadline: "2. Language and typing",
        text: "TypeScript let me build a robust codebase that catches type errors early and improves maintainability. Strict typing keeps data structures consistent and makes future extensions easier."
      },
      {
        subheadline: "3. Styling and animations",
        text: "TailwindCSS 4 transformed my styling system by using the OKLCH color space for more vivid colors and more precise gradients. The CSS-first configuration allowed me to drop a separate tailwind.config.js and define design tokens directly through CSS variables and the @theme directive. For the complex background animations in particular, I used CSS @property definitions combined with CSS variables that transition smoothly between pages. For interactive components like the image gallery, I relied on Framer Motion, whose AnimatePresence component and directional transitions provide fluid enter and exit effects without hurting performance."
      },
      {
        subheadline: "4. Deployment and hosting",
        text: "The website is hosted on Cloudflare Pages, which provides global content delivery network (CDN) distribution, automatic SSL, and CI/CD integration. As a result, the site loads fast worldwide and is served securely."
      },
      {
        subheadline: "5. Modular data structure",
        text: "I implemented a modular data structure with TypeScript interfaces that organizes all of the website's content in structured, typed formats. This simplifies maintenance and makes content updates easy."
      },
      {
        subheadline: "6. Testing and quality assurance",
        text: "With Cypress, I implemented end-to-end tests that automatically verify critical user paths and functionality. This ensures new features or changes don't introduce regressions."
      },
      {
        subheadline: "7. Migration to Coolify",
        text: "After running on Cloudflare Pages for a while, I migrated the website to Coolify on my own server. Two drawbacks of the edge setup drove the decision. First, Cloudflare Pages forced a fully edge-runtime architecture, tying me closely to the provider and its Worker platform; even the contact form ran as a separate Cloudflare Worker next to the actual app. Second, edge functions are only instantiated on the first request: that gives you globally low latency once warm, but after idle periods it regularly leads to noticeably slower initial delivery (cold starts), especially for rarely visited routes."
      },
      {
        subheadline: "8. Deployment with Docker and Coolify",
        text: "The new infrastructure is based on a multi-stage Dockerfile that ships Next.js in standalone mode as a classic Node.js server. Coolify handles builds, deployments, SSL certificates, and reverse-proxy configuration, comparable to a self-hosted alternative to Vercel or Cloudflare Pages. The Server Actions now talk directly to the Brevo API, so the separate Cloudflare Worker for the contact form could be removed entirely. The result: a persistent process without cold starts, full control over the runtime environment, and far less vendor lock-in at comparable performance."
      }
    ],
    results: [
      {
        text: "The development resulted in a modern, high-performance portfolio website:"
      },
      {
        listing: [
          "Lighthouse score above 95 in every category (Performance, Accessibility, Best Practices, SEO)",
          "Fast load times under 2 seconds, even on mobile networks",
          "Responsive design that works flawlessly on all devices",
          "Automated deployment pipeline with Cloudflare Pages",
          "Comprehensive testing strategy with Cypress for E2E tests",
          "Modular, well-structured codebase that's easy to extend",
          "WCAG-compliant accessibility for an inclusive user experience",
          "Optimized color palette using the OKLCH color space for a more vibrant look and better gradients, especially visible in the page-specific background animations"
        ]
      },
      {
        text: "The website serves not only as a portfolio but also as a playground for new web technologies and best practices. Thanks to the chosen architecture, I can implement and test new features quickly while still delivering an optimal user experience."
      }
    ],
    technologies: [
      "Next.js 15",
      "React 19",
      "TypeScript 5",
      "TailwindCSS 4",
      "Framer Motion",
      "Cloudflare Pages",
      "SASS",
      "Cypress",
      "ESLint",
      "Vercel",
      "Git/GitHub"
    ],
    codeExamples: [
      {
        language: "typescript",
        code: `// Framer Motion animation for component transitions
export const fadeInAnimationVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.05 * index,
      duration: 0.5,
      ease: [0.43, 0.13, 0.23, 0.96],
    },
  }),
};`,
        description: "Framer Motion animation for smooth fade-in effects with a staggered delay based on the element's index."
      },
      {
        language: "scss",
        code: `@theme {
  // Base colors for the website using the OKLCH color space
  --colorNeutralSolid: oklch(0.15 0.05 270);   // #1e0b32
  --colorNeutralLight: oklch(0.85 0.12 270);   // #ff88ff
  --colorHomeSolid: oklch(0.12 0.05 270);      // #0c0b32
  --colorHomeLight: oklch(0.88 0.1 195);       // #88ffff
  --colorWorkSolid: oklch(0.15 0.08 310);      // #1e0b4a
  --colorWorkLight: oklch(0.85 0.12 320);      // #ff88ff
  // More colors...
  
  // Spacing and other tokens
  --spacing: 0.25rem;
}`,
        description: "Defining design tokens with the OKLCH color space in TailwindCSS v4 for more vivid and precise color values."
      },
      {
        language: "scss",
        code: `@property --homegradient-color1 {
  syntax: "<color>";
  initial-value: rgba(34, 137, 158, 1);
  inherits: true;
}

@property --homegradient-color2 {
  syntax: "<color>";
  initial-value: rgba(41, 41, 186, 1);
  inherits: true;
}

.content--page__background {
  position: fixed;
  width: 100%;
  height: 100vh;
  top: 0;
  z-index: -1;

  --homegradient-color1: rgba(141, 41, 186, 1);
  --homegradient-color2: rgba(100, 012, 155, 1);
  transition: --homegradient-color1 2400ms, --homegradient-color2 400ms;

  background-image: url("pattern.svg"),
    radial-gradient(
      ellipse at -20% -10%,
      rgba(2, 0, 36, 1) 0%,
      var(--homegradient-color1) 60%,
      var(--homegradient-color2) 100%
    );
}`,
        description: "Animated backgrounds using CSS @property and CSS variables defined in the OKLCH color space enable smooth color transitions between different page sections."
      }
    ],
    images: [
      {
        src: "/assets/img/work/lighthouse_thumb.webp",
        zoomSrc: "/assets/img/work/lighthouse_metrics.webp",
        thumbSrc: "/assets/img/work/lighthouse_thumb.webp",
        alt: "Lighthouse Performance Score",
        type: "image",
        title: "Lighthouse Performance",
        caption: "Lighthouse report with excellent scores for Performance, Accessibility, Best Practices, and SEO."
      },
      {
        src: "/assets/img/work/pagespeedinsights_score.webp",
        zoomSrc: "/assets/img/work/pagespeedinsights_score.webp",
        thumbSrc: "/assets/img/work/pagespeedinsights_score.webp",
        alt: "Pagespeed Insights Performance Score",
        type: "image",
        title: "Pagespeed Insights Performance",
        caption: "Pagespeed Insights score with excellent results for Performance, Accessibility, Best Practices, and SEO."
      },
      {
        src: "/assets/img/work/wave_score.webp",
        zoomSrc: "/assets/img/work/wave_score.webp",
        thumbSrc: "/assets/img/work/wave_score.webp",
        alt: "WAVE Performance Score",
        type: "image",
        title: "WAVE Performance",
        caption: "WAVE report with zero errors and very good accessibility results"
      }
    ]
  }
};

export default developmentOfRobinwichmannCom; 
